/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { activateUserAccount } from '../fn/user-account/activate-user-account';
import { ActivateUserAccount$Params } from '../fn/user-account/activate-user-account';
import { assignExternalLogin } from '../fn/user-account/assign-external-login';
import { AssignExternalLogin$Params } from '../fn/user-account/assign-external-login';
import { changePassword } from '../fn/user-account/change-password';
import { ChangePassword$Params } from '../fn/user-account/change-password';
import { disableTwoFactorEmail } from '../fn/user-account/disable-two-factor-email';
import { DisableTwoFactorEmail$Params } from '../fn/user-account/disable-two-factor-email';
import { enableTwoFactorAuthenticator } from '../fn/user-account/enable-two-factor-authenticator';
import { EnableTwoFactorAuthenticator$Params } from '../fn/user-account/enable-two-factor-authenticator';
import { EnableTwoFactorAuthenticatorResponseDto } from '../models/enable-two-factor-authenticator-response-dto';
import { enableTwoFactorEmail } from '../fn/user-account/enable-two-factor-email';
import { EnableTwoFactorEmail$Params } from '../fn/user-account/enable-two-factor-email';
import { generateNewTwoFactorAuthenticatorKey } from '../fn/user-account/generate-new-two-factor-authenticator-key';
import { GenerateNewTwoFactorAuthenticatorKey$Params } from '../fn/user-account/generate-new-two-factor-authenticator-key';
import { generateTwoFactorRecoveryCodes } from '../fn/user-account/generate-two-factor-recovery-codes';
import { GenerateTwoFactorRecoveryCodes$Params } from '../fn/user-account/generate-two-factor-recovery-codes';
import { GenerateTwoFactorRecoveryCodesResponseDto } from '../models/generate-two-factor-recovery-codes-response-dto';
import { getUserAccountInfo } from '../fn/user-account/get-user-account-info';
import { GetUserAccountInfo$Params } from '../fn/user-account/get-user-account-info';
import { registerExternalUserAccount } from '../fn/user-account/register-external-user-account';
import { RegisterExternalUserAccount$Params } from '../fn/user-account/register-external-user-account';
import { registerSixbidUserAccount } from '../fn/user-account/register-sixbid-user-account';
import { RegisterSixbidUserAccount$Params } from '../fn/user-account/register-sixbid-user-account';
import { registerUserAccount } from '../fn/user-account/register-user-account';
import { RegisterUserAccount$Params } from '../fn/user-account/register-user-account';
import { registerV1UserAccount } from '../fn/user-account/register-v-1-user-account';
import { RegisterV1UserAccount$Params } from '../fn/user-account/register-v-1-user-account';
import { removeTwoFactorAuthenticator } from '../fn/user-account/remove-two-factor-authenticator';
import { RemoveTwoFactorAuthenticator$Params } from '../fn/user-account/remove-two-factor-authenticator';
import { requestPasswordReset } from '../fn/user-account/request-password-reset';
import { RequestPasswordReset$Params } from '../fn/user-account/request-password-reset';
import { resetPassword } from '../fn/user-account/reset-password';
import { ResetPassword$Params } from '../fn/user-account/reset-password';
import { sendActivationCode } from '../fn/user-account/send-activation-code';
import { SendActivationCode$Params } from '../fn/user-account/send-activation-code';
import { unassignExternalLogin } from '../fn/user-account/unassign-external-login';
import { UnassignExternalLogin$Params } from '../fn/user-account/unassign-external-login';
import { UserAccountInfoDto } from '../models/user-account-info-dto';

@Injectable({ providedIn: 'root' })
export class GeneratedUserAccountService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getUserAccountInfo()` */
  static readonly GetUserAccountInfoPath = '/api/user-account';

  /**
   * Gets account information about the signed in user.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUserAccountInfo()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserAccountInfo$Response(params?: GetUserAccountInfo$Params, context?: HttpContext): Observable<StrictHttpResponse<UserAccountInfoDto>> {
    return getUserAccountInfo(this.http, this.rootUrl, params, context);
  }

  /**
   * Gets account information about the signed in user.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUserAccountInfo$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserAccountInfo(params?: GetUserAccountInfo$Params, context?: HttpContext): Observable<UserAccountInfoDto> {
    return this.getUserAccountInfo$Response(params, context).pipe(
      map((r: StrictHttpResponse<UserAccountInfoDto>): UserAccountInfoDto => r.body)
    );
  }

  /** Path part for operation `registerUserAccount()` */
  static readonly RegisterUserAccountPath = '/api/user-account';

  /**
   * Registers a new user account with password login.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `registerUserAccount()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerUserAccount$Response(params: RegisterUserAccount$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return registerUserAccount(this.http, this.rootUrl, params, context);
  }

  /**
   * Registers a new user account with password login.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `registerUserAccount$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerUserAccount(params: RegisterUserAccount$Params, context?: HttpContext): Observable<void> {
    return this.registerUserAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `registerExternalUserAccount()` */
  static readonly RegisterExternalUserAccountPath = '/api/user-account/external';

  /**
   * Registers a new user account with an external login.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `registerExternalUserAccount()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerExternalUserAccount$Response(params: RegisterExternalUserAccount$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return registerExternalUserAccount(this.http, this.rootUrl, params, context);
  }

  /**
   * Registers a new user account with an external login.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `registerExternalUserAccount$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerExternalUserAccount(params: RegisterExternalUserAccount$Params, context?: HttpContext): Observable<void> {
    return this.registerExternalUserAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `registerSixbidUserAccount()` */
  static readonly RegisterSixbidUserAccountPath = '/api/user-account/sixbid';

  /**
   * Registers a new user account with a sixbid login.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `registerSixbidUserAccount()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerSixbidUserAccount$Response(params: RegisterSixbidUserAccount$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return registerSixbidUserAccount(this.http, this.rootUrl, params, context);
  }

  /**
   * Registers a new user account with a sixbid login.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `registerSixbidUserAccount$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerSixbidUserAccount(params: RegisterSixbidUserAccount$Params, context?: HttpContext): Observable<void> {
    return this.registerSixbidUserAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `registerV1UserAccount()` */
  static readonly RegisterV1UserAccountPath = '/api/user-account/v1';

  /**
   * Registers a new user account by retrieving its data from Auex V1.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `registerV1UserAccount()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerV1UserAccount$Response(params: RegisterV1UserAccount$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return registerV1UserAccount(this.http, this.rootUrl, params, context);
  }

  /**
   * Registers a new user account by retrieving its data from Auex V1.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `registerV1UserAccount$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerV1UserAccount(params: RegisterV1UserAccount$Params, context?: HttpContext): Observable<void> {
    return this.registerV1UserAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `activateUserAccount()` */
  static readonly ActivateUserAccountPath = '/api/user-account/activation-code';

  /**
   * Activates a registered account.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `activateUserAccount()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  activateUserAccount$Response(params: ActivateUserAccount$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return activateUserAccount(this.http, this.rootUrl, params, context);
  }

  /**
   * Activates a registered account.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `activateUserAccount$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  activateUserAccount(params: ActivateUserAccount$Params, context?: HttpContext): Observable<void> {
    return this.activateUserAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `sendActivationCode()` */
  static readonly SendActivationCodePath = '/api/user-account/resend-activation-code';

  /**
   * Sends an activation code to the given user.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `sendActivationCode()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  sendActivationCode$Response(params: SendActivationCode$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return sendActivationCode(this.http, this.rootUrl, params, context);
  }

  /**
   * Sends an activation code to the given user.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `sendActivationCode$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  sendActivationCode(params: SendActivationCode$Params, context?: HttpContext): Observable<void> {
    return this.sendActivationCode$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `changePassword()` */
  static readonly ChangePasswordPath = '/api/user-account/password';

  /**
   * Change the password of the logged in user.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `changePassword()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  changePassword$Response(params: ChangePassword$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return changePassword(this.http, this.rootUrl, params, context);
  }

  /**
   * Change the password of the logged in user.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `changePassword$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  changePassword(params: ChangePassword$Params, context?: HttpContext): Observable<void> {
    return this.changePassword$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `assignExternalLogin()` */
  static readonly AssignExternalLoginPath = '/api/user-account/external-logins';

  /**
   * Adds a new external login to a given password-login.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `assignExternalLogin()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  assignExternalLogin$Response(params: AssignExternalLogin$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return assignExternalLogin(this.http, this.rootUrl, params, context);
  }

  /**
   * Adds a new external login to a given password-login.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `assignExternalLogin$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  assignExternalLogin(params: AssignExternalLogin$Params, context?: HttpContext): Observable<void> {
    return this.assignExternalLogin$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `unassignExternalLogin()` */
  static readonly UnassignExternalLoginPath = '/api/user-account/external-logins/{loginProvider}';

  /**
   * Removes an external login from a given password-login.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `unassignExternalLogin()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  unassignExternalLogin$Response(params: UnassignExternalLogin$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return unassignExternalLogin(this.http, this.rootUrl, params, context);
  }

  /**
   * Removes an external login from a given password-login.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `unassignExternalLogin$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  unassignExternalLogin(params: UnassignExternalLogin$Params, context?: HttpContext): Observable<void> {
    return this.unassignExternalLogin$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `enableTwoFactorEmail()` */
  static readonly EnableTwoFactorEmailPath = '/api/user-account/two-factor/email';

  /**
   * Enables two-factor authentication via mail code.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `enableTwoFactorEmail()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  enableTwoFactorEmail$Response(params: EnableTwoFactorEmail$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return enableTwoFactorEmail(this.http, this.rootUrl, params, context);
  }

  /**
   * Enables two-factor authentication via mail code.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `enableTwoFactorEmail$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  enableTwoFactorEmail(params: EnableTwoFactorEmail$Params, context?: HttpContext): Observable<void> {
    return this.enableTwoFactorEmail$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `disableTwoFactorEmail()` */
  static readonly DisableTwoFactorEmailPath = '/api/user-account/two-factor/email';

  /**
   * Disables two-factor authentication via mail code.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disableTwoFactorEmail()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  disableTwoFactorEmail$Response(params: DisableTwoFactorEmail$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return disableTwoFactorEmail(this.http, this.rootUrl, params, context);
  }

  /**
   * Disables two-factor authentication via mail code.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disableTwoFactorEmail$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  disableTwoFactorEmail(params: DisableTwoFactorEmail$Params, context?: HttpContext): Observable<void> {
    return this.disableTwoFactorEmail$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `generateNewTwoFactorAuthenticatorKey()` */
  static readonly GenerateNewTwoFactorAuthenticatorKeyPath = '/api/user-account/two-factor/authenticator/key';

  /**
   * Generates a new authenticator code.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateNewTwoFactorAuthenticatorKey()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateNewTwoFactorAuthenticatorKey$Response(params?: GenerateNewTwoFactorAuthenticatorKey$Params, context?: HttpContext): Observable<StrictHttpResponse<EnableTwoFactorAuthenticatorResponseDto>> {
    return generateNewTwoFactorAuthenticatorKey(this.http, this.rootUrl, params, context);
  }

  /**
   * Generates a new authenticator code.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `generateNewTwoFactorAuthenticatorKey$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateNewTwoFactorAuthenticatorKey(params?: GenerateNewTwoFactorAuthenticatorKey$Params, context?: HttpContext): Observable<EnableTwoFactorAuthenticatorResponseDto> {
    return this.generateNewTwoFactorAuthenticatorKey$Response(params, context).pipe(
      map((r: StrictHttpResponse<EnableTwoFactorAuthenticatorResponseDto>): EnableTwoFactorAuthenticatorResponseDto => r.body)
    );
  }

  /** Path part for operation `enableTwoFactorAuthenticator()` */
  static readonly EnableTwoFactorAuthenticatorPath = '/api/user-account/two-factor/authenticator';

  /**
   * Enables two-factor authentication via authenticator.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `enableTwoFactorAuthenticator()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  enableTwoFactorAuthenticator$Response(params: EnableTwoFactorAuthenticator$Params, context?: HttpContext): Observable<StrictHttpResponse<GenerateTwoFactorRecoveryCodesResponseDto>> {
    return enableTwoFactorAuthenticator(this.http, this.rootUrl, params, context);
  }

  /**
   * Enables two-factor authentication via authenticator.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `enableTwoFactorAuthenticator$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  enableTwoFactorAuthenticator(params: EnableTwoFactorAuthenticator$Params, context?: HttpContext): Observable<GenerateTwoFactorRecoveryCodesResponseDto> {
    return this.enableTwoFactorAuthenticator$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenerateTwoFactorRecoveryCodesResponseDto>): GenerateTwoFactorRecoveryCodesResponseDto => r.body)
    );
  }

  /** Path part for operation `removeTwoFactorAuthenticator()` */
  static readonly RemoveTwoFactorAuthenticatorPath = '/api/user-account/two-factor/authenticator';

  /**
   * Disables two-factor authentication via authenticator.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `removeTwoFactorAuthenticator()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  removeTwoFactorAuthenticator$Response(params: RemoveTwoFactorAuthenticator$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return removeTwoFactorAuthenticator(this.http, this.rootUrl, params, context);
  }

  /**
   * Disables two-factor authentication via authenticator.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `removeTwoFactorAuthenticator$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  removeTwoFactorAuthenticator(params: RemoveTwoFactorAuthenticator$Params, context?: HttpContext): Observable<void> {
    return this.removeTwoFactorAuthenticator$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `generateTwoFactorRecoveryCodes()` */
  static readonly GenerateTwoFactorRecoveryCodesPath = '/api/user-account/two-factor/recovery-codes';

  /**
   * Generates new one-time recovery-codes as second factor.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateTwoFactorRecoveryCodes()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  generateTwoFactorRecoveryCodes$Response(params: GenerateTwoFactorRecoveryCodes$Params, context?: HttpContext): Observable<StrictHttpResponse<GenerateTwoFactorRecoveryCodesResponseDto>> {
    return generateTwoFactorRecoveryCodes(this.http, this.rootUrl, params, context);
  }

  /**
   * Generates new one-time recovery-codes as second factor.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `generateTwoFactorRecoveryCodes$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  generateTwoFactorRecoveryCodes(params: GenerateTwoFactorRecoveryCodes$Params, context?: HttpContext): Observable<GenerateTwoFactorRecoveryCodesResponseDto> {
    return this.generateTwoFactorRecoveryCodes$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenerateTwoFactorRecoveryCodesResponseDto>): GenerateTwoFactorRecoveryCodesResponseDto => r.body)
    );
  }

  /** Path part for operation `requestPasswordReset()` */
  static readonly RequestPasswordResetPath = '/api/user-account/password-resets';

  /**
   * Request a password reset.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `requestPasswordReset()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  requestPasswordReset$Response(params: RequestPasswordReset$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return requestPasswordReset(this.http, this.rootUrl, params, context);
  }

  /**
   * Request a password reset.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `requestPasswordReset$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  requestPasswordReset(params: RequestPasswordReset$Params, context?: HttpContext): Observable<void> {
    return this.requestPasswordReset$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `resetPassword()` */
  static readonly ResetPasswordPath = '/api/user-account/password-resets/token';

  /**
   * Sets a password via a reset token.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `resetPassword()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  resetPassword$Response(params?: ResetPassword$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return resetPassword(this.http, this.rootUrl, params, context);
  }

  /**
   * Sets a password via a reset token.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `resetPassword$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  resetPassword(params?: ResetPassword$Params, context?: HttpContext): Observable<void> {
    return this.resetPassword$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

}
